/**
 * Waivers - Pending / Processed / Failed tabs.
 * Submit + cancel claim. Free-agent direct-add path included.
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Trash2, Inbox, Check, X, Plus, RefreshCw } from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { useSeason } from "../contexts/SeasonContext";
import { useAuth, authHeaders } from "../contexts/AuthContext";
import SeasonShell from "../components/season/SeasonShell";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Waivers() {
  const { id: leagueId } = useParams();
  const { isDark } = useTheme();
  const { actingAsTeamId } = useSeason();
  const { user } = useAuth();
  const [tab, setTab] = useState("PENDING");
  const [claims, setClaims] = useState([]);
  const [season, setSeason] = useState(null);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const myTeam = season?.teams?.find((t) => t.id === actingAsTeamId)
              || season?.teams?.find((t) => t.user_id === user?.id);

  const refresh = async () => {
    try {
      const [s, c] = await Promise.all([
        axios.get(`${API}/season/${leagueId}`, { headers: authHeaders() }),
        axios.get(`${API}/waivers/${leagueId}?status=${tab}`, { headers: authHeaders() }),
      ]);
      setSeason(s.data);
      setClaims(c.data || []);
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  useEffect(() => { refresh(); }, [leagueId, tab]);

  const handleProcess = async () => {
    setError(null); setInfo(null);
    try {
      const r = await axios.post(`${API}/waivers/${leagueId}/process`, {}, { headers: authHeaders() });
      setInfo(`${r.data.processed} claim(s) processed.`);
      await refresh();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  const handleCancel = async (claimId) => {
    try {
      await axios.delete(`${API}/waivers/${claimId}`, { headers: authHeaders() });
      await refresh();
    } catch (e) { setError(e?.response?.data?.detail || e.message); }
  };

  const muted = isDark ? "text-[#A0A0A0]" : "text-[#404040]";
  const fg = isDark ? "text-[#F5F5F5]" : "text-[#111111]";
  const cardBg = isDark ? "bg-[#1C1C1C]" : "bg-white";
  const cardBorder = isDark ? "border-[#2A2A2A]" : "border-[#D0D0D0]";

  return (
    <SeasonShell>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF6B00] font-display font-bold mb-1">Waivers</div>
          <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">
            FAAB ${myTeam?.faab_remaining ?? "-"} <span className="text-base text-[#A0A0A0]">remaining</span>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-white text-sm font-display font-bold uppercase tracking-wide" data-testid="waivers-new-claim">
            <Plus size={14} /> New claim
          </button>
          <button onClick={handleProcess} className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#00C853]/15 border border-[#00C853]/40 text-[#00C853] hover:bg-[#00C853]/25 text-sm font-display font-bold uppercase tracking-wide" data-testid="waivers-process">
            <RefreshCw size={14} /> Process now
          </button>
        </div>
      </div>

      {error && <div className="mb-3 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/40 text-red-400 text-sm" data-testid="waivers-error">{error}</div>}
      {info && <div className="mb-3 px-4 py-2 rounded-lg bg-[#00C853]/10 border border-[#00C853]/40 text-[#00C853] text-sm">{info}</div>}

      <div className="flex gap-1 mb-4">
        {["PENDING", "SUCCEEDED", "FAILED", "CANCELLED"].map((s) => (
          <button
            key={s}
            onClick={() => setTab(s)}
            className={`px-3 py-1.5 rounded-lg text-xs font-display font-bold uppercase tracking-wide transition ${
              tab === s ? "bg-[#FF6B00]/15 text-[#FF6B00]" : `${muted} hover:${fg} border ${cardBorder}`
            }`}
            data-testid={`waivers-tab-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className={`rounded-2xl ${cardBg} border ${cardBorder} overflow-hidden`} data-testid="waivers-list">
        {claims.length === 0 ? (
          <div className={`p-8 text-center ${muted} text-sm flex flex-col items-center gap-2`}>
            <Inbox size={28} /> No claims in this state.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className={`text-[10px] uppercase tracking-widest ${muted}`}>
              <tr className={isDark ? "bg-[#252525]" : "bg-[#F5F5F5]"}>
                <th className="px-3 py-2.5 text-left">Team</th>
                <th className="px-3 py-2.5 text-left">Add</th>
                <th className="px-3 py-2.5 text-left">Drop</th>
                <th className="px-3 py-2.5 text-right">FAAB</th>
                <th className="px-3 py-2.5 text-left">Status</th>
                <th className="px-3 py-2.5 text-right">-</th>
              </tr>
            </thead>
            <tbody>
              {claims.map((c) => (
                <tr key={c.id} className={isDark ? "border-t border-[#2A2A2A]" : "border-t border-[#E0E0E0]"} data-testid={`waivers-row-${c.id}`}>
                  <td className="px-3 py-2 font-display font-bold uppercase text-xs">{c.team_name}</td>
                  <td className="px-3 py-2 text-xs">{c.player_to_add_name || "-"}</td>
                  <td className="px-3 py-2 text-xs">{c.player_to_drop_name || "-"}</td>
                  <td className="px-3 py-2 text-right font-display font-black text-[#00C853]">${c.faab_amount}</td>
                  <td className="px-3 py-2 text-xs">
                    <StatusPill status={c.status} reason={c.failure_reason} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    {c.status === "PENDING" && c.team_id === myTeam?.id && (
                      <button onClick={() => handleCancel(c.id)} className="text-red-400 hover:text-red-300" data-testid={`waivers-cancel-${c.id}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && myTeam && (
        <ClaimModal
          isDark={isDark}
          leagueId={leagueId}
          team={myTeam}
          onClose={() => setShowAdd(false)}
          onSuccess={() => { setShowAdd(false); setTab("PENDING"); refresh(); }}
        />
      )}
    </SeasonShell>
  );
}


function StatusPill({ status, reason }) {
  const map = {
    PENDING: "bg-yellow-500/15 text-yellow-400",
    SUCCEEDED: "bg-[#00C853]/15 text-[#00C853]",
    FAILED: "bg-red-500/15 text-red-400",
    CANCELLED: "bg-gray-500/15 text-gray-400",
  };
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-display font-bold ${map[status] || ""}`} title={reason || undefined}>
      {status}
    </span>
  );
}


function ClaimModal({ isDark, leagueId, team, onClose, onSuccess }) {
  const [players, setPlayers] = useState([]);
  const [add, setAdd] = useState(null);
  const [drop, setDrop] = useState(null);
  const [faab, setFaab] = useState(0);
  const [q, setQ] = useState("");
  const [roster, setRoster] = useState([]);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // Modal-scoped fetches: independent, surface error state if either fails.
    axios.get(`${API}/players`, { headers: authHeaders() })
      .then((p) => setPlayers(p.data?.players || p.data || []))
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Failed to load players"));
    axios.get(`${API}/teams/${team.id}/roster`, { headers: authHeaders() })
      .then((r) => setRoster(r.data || []))
      .catch((e) => setError(e?.response?.data?.detail || e?.message || "Failed to load roster"));
  }, [team.id]);

  const rosteredIds = new Set(roster.map((r) => r.player.id));
  const filtered = players
    .filter((p) => !rosteredIds.has(p.id))
    .filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (b.projected_points || 0) - (a.projected_points || 0))
    .slice(0, 30);

  const submit = async () => {
    if (!add) return;
    setBusy(true); setError(null);
    try {
      await axios.post(`${API}/waivers/${leagueId}`, {
        team_id: team.id,
        player_to_add_id: add.id,
        player_to_drop_id: drop?.id || null,
        faab_amount: parseInt(faab, 10) || 0,
      }, { headers: authHeaders() });
      onSuccess();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const cardBg = isDark ? "bg-[#181818]" : "bg-white";
  const cardBorder = isDark ? "border-[#2A2A2A]" : "border-[#D0D0D0]";
  const muted = isDark ? "text-[#A0A0A0]" : "text-[#404040]";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className={`rounded-2xl ${cardBg} border ${cardBorder} max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col`} onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-[#2A2A2A] flex items-center justify-between">
          <span className="text-[10px] uppercase tracking-widest text-[#FF6B00] font-display font-bold">New Waiver Claim</span>
          <button onClick={onClose} className={muted}><X size={16} /></button>
        </div>

        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          <div>
            <div className={`text-[10px] uppercase tracking-widest ${muted} mb-1`}>Add</div>
            {add ? (
              <div className="flex items-center justify-between p-2 rounded-lg bg-[#00C853]/10 text-sm">
                <span>{add.name} - {add.position} · {add.nfl_team}</span>
                <button onClick={() => setAdd(null)} className="text-red-400"><X size={14} /></button>
              </div>
            ) : (
              <input
                type="text" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search free agents…"
                className={`w-full px-3 py-2 rounded-lg ${isDark ? "bg-[#252525] text-white" : "bg-[#F0F0F0] text-black"} border ${cardBorder} text-sm`}
                data-testid="claim-search"
              />
            )}
            {!add && q && (
              <div className="mt-2 max-h-40 overflow-y-auto space-y-1">
                {filtered.map((p) => (
                  <button
                    key={p.id} onClick={() => { setAdd(p); setQ(""); }}
                    className="w-full text-left px-3 py-1.5 rounded-lg hover:bg-[#FF6B00]/10 text-xs"
                    data-testid={`claim-add-${p.id}`}
                  >
                    <span className="font-display font-bold uppercase">{p.name}</span> - {p.position} · {p.nfl_team} · Proj {Math.round(p.projected_points || 0)}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <div className={`text-[10px] uppercase tracking-widest ${muted} mb-1`}>Drop (optional)</div>
            <select
              value={drop?.id || ""}
              onChange={(e) => {
                const r = roster.find((x) => x.player.id === e.target.value);
                setDrop(r?.player || null);
              }}
              className={`w-full px-3 py-2 rounded-lg ${isDark ? "bg-[#252525] text-white" : "bg-[#F0F0F0] text-black"} border ${cardBorder} text-sm`}
              data-testid="claim-drop"
            >
              <option value="">- No drop -</option>
              {roster.map((r) => (
                <option key={r.player.id} value={r.player.id}>{r.player.name} ({r.player.position})</option>
              ))}
            </select>
          </div>

          <div>
            <div className={`text-[10px] uppercase tracking-widest ${muted} mb-1`}>FAAB Bid</div>
            <input
              type="number" min="0" max={team.faab_remaining || 100}
              value={faab} onChange={(e) => setFaab(e.target.value)}
              className={`w-full px-3 py-2 rounded-lg ${isDark ? "bg-[#252525] text-white" : "bg-[#F0F0F0] text-black"} border ${cardBorder} text-sm`}
              data-testid="claim-faab"
            />
            <div className={`text-[10px] ${muted} mt-1`}>
              Free agents (no recent drops) submit at $0 with instant-add. Otherwise FAAB applies on next process.
            </div>
          </div>

          {error && <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/40 text-red-400 text-xs">{error}</div>}
        </div>

        <div className="p-4 border-t border-[#2A2A2A]">
          <button
            onClick={submit} disabled={!add || busy}
            className="w-full px-4 py-2.5 rounded-lg bg-[#FF6B00] hover:bg-[#FF8533] text-white font-display font-bold uppercase tracking-wide text-sm disabled:opacity-50"
            data-testid="claim-submit"
          >
            {busy ? "Submitting…" : "Submit Claim"}
          </button>
        </div>
      </div>
    </div>
  );
}
